import { type CreatePanelInput, type PanelDetail, createPanelSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { FormActions } from '#client/components/ui/FormActions';
import { NumberField } from '#client/components/ui/NumberField';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { initialPanelValues } from './panel-form.logic';

const PATH = RESOURCE.panels;

export function PanelFormPage() {
  return (
    <ResourceFormShell<PanelDetail> path={PATH} noun="panel">
      {({ id, record }) => <PanelForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

/** Routing-free, so it can also be mounted outside the router. */
function PanelForm({ id, record }: { id?: string | undefined; record?: PanelDetail | undefined }) {
  const { values, set } = useFormValues<CreatePanelInput>(initialPanelValues(record));

  const form = useResourceForm({ path: PATH, id, schema: createPanelSchema, noun: 'Panel' });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card>
          <div className="c-form-grid">
            <TextField
              label="Name"
              value={values.name}
              error={form.errors.name}
              onChange={(event) => set('name', event.target.value)}
              required
            />
            <TextField
              label="Code"
              value={values.code}
              error={form.errors.code}
              onChange={(event) => set('code', event.target.value)}
              required
            />
            <TextField
              label="Owner name"
              value={values.owner_name}
              error={form.errors.owner_name}
              onChange={(event) => set('owner_name', event.target.value)}
              required
            />
            <NumberField
              label="Discount"
              min={0}
              max={100}
              step={0.01}
              value={values.discount_percentage}
              error={form.errors.discount_percentage}
              onChange={(value) => set('discount_percentage', value)}
              prefix="%"
              required
            />
            <TextField
              label="Phone number"
              value={values.phone_number}
              error={form.errors.phone_number}
              onChange={(event) => set('phone_number', event.target.value)}
              required
            />
            <TextField
              label="Email"
              type="email"
              value={values.email}
              error={form.errors.email}
              onChange={(event) => set('email', event.target.value)}
              required
            />
          </div>

          <TextArea
            label="Contract notes"
            value={values.contract ?? ''}
            error={form.errors.contract}
            onChange={(event) => set('contract', event.target.value)}
            rows={4}
          />
        </Card>

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Create panel'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
